import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppShell } from '@/components/app-shell';
import { ALL_NAV_ITEMS } from '@/nav';
import { AuditPage } from '@/routes/audit';
import { ConnectionsPage } from '@/routes/connections';
import { CredentialsPage } from '@/routes/credentials';
import { DashboardPage } from '@/routes/dashboard';
import { ApprovalsPage } from '@/routes/approvals';
import { PoliciesPage, WorkflowsPage } from '@/routes/governance';
import { ExecutionDetailPage, ExecutionsPage } from '@/routes/executions';
import { KnowledgePage } from '@/routes/knowledge';
import { LlmSettingsPage } from '@/routes/llm-settings';
import { McpRegistryPage } from '@/routes/mcp-registry';
import { PlannedPage } from '@/routes/planned';
import { PromptsPage } from '@/routes/prompts';
import { SessionPage } from '@/routes/session-page';
import { SystemHealthPage } from '@/routes/system-health';

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
  notFoundComponent: () => <p className="text-sm text-muted">Page not found.</p>,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // TanStack Router's redirect protocol throws a redirect object by design.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/dashboard' });
  },
});
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: DashboardPage,
});
const executionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/executions',
  component: ExecutionsPage,
});
const executionDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/executions/$executionId',
  component: ExecutionDetailPage,
});
const workflowsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workflows',
  component: WorkflowsPage,
});
const policiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/policies',
  component: PoliciesPage,
});
const approvalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/approvals',
  component: ApprovalsPage,
});
const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: AuditPage,
});
const credentialsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/credentials',
  component: CredentialsPage,
});
const healthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/system/health',
  component: SystemHealthPage,
});
const connectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connections',
  component: ConnectionsPage,
});
const mcpRegistryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/mcp-registry',
  component: McpRegistryPage,
});
const promptsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prompts',
  component: PromptsPage,
});
const knowledgeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/knowledge',
  component: KnowledgePage,
});
const llmRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/llm',
  component: LlmSettingsPage,
});
const sessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/session',
  component: SessionPage,
});

// Placeholders for sections planned in later increments (typed loosely on purpose).
const plannedRoutes = ALL_NAV_ITEMS.filter((i) => !i.available).map((item) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: item.path as '/planned',
    component: () => <PlannedPage item={item} />,
  }),
);

const routeTree = rootRoute.addChildren([
  indexRoute,
  dashboardRoute,
  executionsRoute,
  executionDetailRoute,
  approvalsRoute,
  workflowsRoute,
  policiesRoute,
  auditRoute,
  credentialsRoute,
  healthRoute,
  connectionsRoute,
  mcpRegistryRoute,
  promptsRoute,
  llmRoute,
  knowledgeRoute,
  sessionRoute,
  ...plannedRoutes,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
